// One-time cleanup of push-notification state that a browser may still hold from ANOTHER Firebase project.
//
// Why: before environments were separated, bespokebeauty.cloud (and staging.) shared the dev Firebase project. A browser
// that allowed notifications back then keeps a push subscription, a service worker and cached Firebase push data tied to
// that old project. Firebase then hands back tokens that look valid but are unregistered on Google's side, so campaigns
// silently fail. This module removes that stale state ONCE per browser per Firebase project, before a token is requested.
//
// It only touches the push service worker, its push subscription and Firebase's messaging/installations databases. It
// never touches sign-in data (firebaseLocalStorageDb) or anything else. It never throws.

const MARKER_KEY = 'bespoke_fcm_project';
const FIREBASE_PUSH_DBS = ['firebase-messaging-database', 'firebase-installations-database', 'firebase-messaging-store'];
const PUSH_WORKER_NAME = 'firebase-messaging-sw';

let inFlight = null; // several parts of the app ask for a token at once; reset only once per page load

const defaultEnv = () => ({
  storage: typeof localStorage !== 'undefined' ? localStorage : null,
  serviceWorker: typeof navigator !== 'undefined' ? navigator.serviceWorker : null,
  indexedDB: typeof indexedDB !== 'undefined' ? indexedDB : null,
  blockedTimeoutMs: 3000,
});

const deleteDatabase = (idb, name, timeoutMs) =>
  new Promise((resolve) => {
    let finished = false;
    const done = () => { if (!finished) { finished = true; resolve(); } };
    try {
      const req = idb.deleteDatabase(name);
      req.onsuccess = done;
      req.onerror = done;
      // Blocked = another tab still has it open. It will be deleted when that closes; do not wait forever.
      req.onblocked = () => setTimeout(done, timeoutMs);
      setTimeout(done, timeoutMs + 500);
    } catch (e) {
      done();
    }
  });

/**
 * @param {string} projectId  this build's Firebase project id (REACT_APP_FIREBASE_PROJECT_ID)
 * @param {object} [env]      injectable browser pieces (used by tests)
 * @returns {Promise<boolean>} true if a reset was performed
 */
export const resetPushStateIfProjectChanged = (projectId, env) => {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const e = env || defaultEnv();
      if (!projectId || !e.storage) return false;

      let marker = null;
      try { marker = e.storage.getItem(MARKER_KEY); } catch (err) { /* storage unavailable: treat as unknown */ }
      if (marker === projectId) return false; // already set up for this project

      console.log('Push notifications: clearing push state from a previous setup (one time)...');

      if (e.serviceWorker && e.serviceWorker.getRegistrations) {
        const regs = await e.serviceWorker.getRegistrations();
        for (const reg of regs) {
          const worker = reg.active || reg.waiting || reg.installing;
          if (!worker || !String(worker.scriptURL).includes(PUSH_WORKER_NAME)) continue; // leave other workers alone
          try {
            const sub = reg.pushManager && (await reg.pushManager.getSubscription());
            if (sub) await sub.unsubscribe();
          } catch (err) { /* keep going */ }
          try { await reg.unregister(); } catch (err) { /* keep going */ }
        }
      }

      if (e.indexedDB) {
        const timeout = e.blockedTimeoutMs != null ? e.blockedTimeoutMs : 3000;
        await Promise.all(FIREBASE_PUSH_DBS.map((name) => deleteDatabase(e.indexedDB, name, timeout)));
      }

      try { e.storage.setItem(MARKER_KEY, projectId); } catch (err) { /* ignore */ }
      return true;
    } catch (err) {
      console.log('Push state reset skipped:', err && err.message);
      return false;
    }
  })();
  return inFlight;
};

// Test helper: lets tests run the function more than once in one process.
export const _resetForTests = () => { inFlight = null; };
